import React from 'react';
import {
  AppBar,
  Box,
  Button,
  Dialog,
  Divider,
  Grid,
  IconButton,
  Paper,
  Slide,
  Toolbar,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { TransitionProps } from '@mui/material/transitions';
import { ExportJob } from '@/API.ts';
import { FilterDefinition } from '@lambda-layer/models/FilterDefinition/FilterDefinition.ts';
import GradingIcon from '@mui/icons-material/Grading';
import { FilterSelections } from '@lambda-layer/types/filter';
import { formatFileSize } from '@/utils/fileManagerUtils';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>
) {
  return (
    <Slide direction="up" ref={ref} {...props}>
      {props.children}
    </Slide>
  );
});

interface JobDetailDialogProps {
  filterDefinitions: FilterDefinition[];
  job: ExportJob;
  open: boolean;
  onClose: () => void;
  onApplyUserFilter?: (filterSelections: FilterSelections) => void;
}

// Helper function to convert Filter input JSON to a readable format
const parseAppliedFilter = (
  filterDefinitions: FilterDefinition[],
  filterInput: string
): string[] => {
  const filters = JSON.parse(filterInput) as FilterSelections;
  const formattedFilters: string[] = [];

  filterDefinitions
    .filter(f => f.label)
    .forEach(f => {
      const label = f.label;
      const filterSelection = filters[f.id];
      const relationalOperator = filterSelection?.relationalOperator;
      const values = filterSelection?.value || [];
      if (values.length > 0) {
        formattedFilters.push(
          `${label} ${relationalOperator} ${values.map(value => `"${value}"`).join(', ')}`
        );
      }
    });

  return formattedFilters;
};

// Format dates for better readability
const formatDate = (date: string): string => {
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  };
  return new Date(date).toLocaleString('en-US', options);
};

/**
 * Job detail dialog
 */
const JobDetailDialog: React.FC<JobDetailDialogProps> = ({
  filterDefinitions, // Full list of filter definitions with user-facing properties (label, tooltip, etc.)
  job,
  open,
  onClose,
  onApplyUserFilter,
}) => {
  const appliedFilters = parseAppliedFilter(filterDefinitions, job.filterInput);

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
    >
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
            Export Job Details
          </Typography>
          <Button autoFocus color="inherit" onClick={onClose}>
            Close
          </Button>
        </Toolbar>
      </AppBar>
      <Box sx={{ p: 3 }}>
        <Grid container spacing={2} direction="column">
          <Grid item>
            <Typography variant="h6">Job Information</Typography>
          </Grid>
          <Divider />
          <Grid item aria-label="export name">
            <Typography>
              <strong>Export Name:</strong> {job.exportName}
            </Typography>
          </Grid>
          <Grid item aria-label="status">
            <Typography>
              <strong>Status:</strong> {job.status}
            </Typography>
          </Grid>
          <Grid item aria-label="filter">
            <Typography variant="h6" sx={{ mt: 2 }}>
              Filter:
            </Typography>
            <Paper elevation={2} sx={{ p: 2, mt: 1 }}>
              {appliedFilters.map((filter, index) => (
                <Typography key={index}>{filter}</Typography>
              ))}
            </Paper>
            {onApplyUserFilter && (
              <Button
                onClick={() => {
                  onApplyUserFilter(JSON.parse(job.filterInput));
                }}
                size={'small'}
                startIcon={<GradingIcon />}
              >
                Apply List Filters
              </Button>
            )}
          </Grid>
          <Grid item aria-label="total records">
            <Typography>
              <strong>Total Records:</strong> {job.totalRecords}
            </Typography>
          </Grid>
          <Grid item aria-label="file size">
            <Typography>
              <strong>File Size:</strong>{' '}
              {formatFileSize(job.fileSize || 0, {
                decimals: 2,
                units: ['bytes', 'KB', 'MB', 'GB', 'TB'],
                zeroDisplay: '0 bytes',
              })}
            </Typography>
          </Grid>
          <Grid item aria-label="export date">
            <Typography>
              <strong>Export Date:</strong>{' '}
              {job.startedAt ? formatDate(job.startedAt) : 'Not Available'}
            </Typography>
          </Grid>
          <Grid item aria-label="expires at">
            <Typography>
              <strong>Expires At:</strong>{' '}
              {job.expiresAt ? formatDate(job.expiresAt) : 'Not Available'}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Dialog>
  );
};

export default JobDetailDialog;
